import { Fragment, ReactNode, useState, useContext } from "react"
import { AlertMessage } from "source/shared/components"
import {
  FindVerifiedPerson,
  communicateErrorAsHelpfulAsPossible,
} from "source/login/FindVerifiedPerson"
import { sessionApiClient } from "@planningcenter/cc-api-client"
// @ts-expect-error - No types available for this module
import { resetCache as resetNavitarCache } from "@planningcenter/cc-nav"
import { resetCache as resetWebBootCache } from "source/publishing/WebBoot"
import { DynamicSessionContext } from "source/shared/contexts/DynamicSession"
import { LoginFormBaseProps } from "./types"

/**
 * Response from POST /global/v2/verified_contacts/{deviceId}/people/{personId}/authenticate
 * This represents an authenticated person with their verification token.
 */
interface AuthenticatedPersonResponse {
  data: {
    id: string
    type: string
    attributes: {
      name: string
      avatar_url: string
      email_address: string
      phone_number: string | null
      token: {
        data: {
          attributes: {
            token: string
          }
        }
      }
    }
  }
}

interface EmbeddedLoginFormProps extends LoginFormBaseProps {}

/**
 * EmbeddedLoginForm - Login component for iframe/embedded contexts
 *
 * This component handles authentication in embedded contexts (like iframes)
 * where third-party cookies are blocked. Instead of establishing a browser
 * session via cookies, it uses the ChurchCenterPersonToken directly as a
 * bearer token for API authentication.
 */
export function EmbeddedLoginForm({
  emailOnly,
  textOverrides,
  onSuccess,
}: EmbeddedLoginFormProps) {
  const [error, setError] = useState<false | ReactNode>(false)
  const [key, setKey] = useState(0)
  const context = useContext(DynamicSessionContext)

  async function loginAndRedirect(
    person: AuthenticatedPersonResponse,
  ): Promise<void> {
    try {
      // Extract the ChurchCenterPersonToken from the nested structure
      const token = person.data.attributes.token.data.attributes.token

      // Set the token directly as the authorization header for all API calls
      // This bypasses the need for browser session cookies
      sessionApiClient.setAuthorizationToken({
        data: {
          type: "Bearer",
          attributes: { token },
        },
      })

      // Validate token and update DynamicSession with person data
      // This sets embedded auth mode and fetches complete person data
      if (!context) {
        throw new Error(
          "DynamicSessionContext is not available. Ensure EmbeddedLoginForm is used within DynamicSessionProvider.",
        )
      }
      await context.refreshSessionFromToken(token)

      // Reset caches to ensure fresh data with new auth context
      resetNavitarCache()
      resetWebBootCache()

      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      setError(communicateErrorAsHelpfulAsPossible(err as Error))
    }
  }

  function restart() {
    setError(false)
    setKey((prev) => prev + 1)
  }

  const params = new URLSearchParams(window.location.search)
  const defaultValue = params.get("value") || ""
  const autoSubmitDefaultValue = false // params.has("autosubmit") // NOTE: Disabled temporarily. See Pull Request #287 for details.

  return error ? (
    <Fragment>
      <AlertMessage>{error}</AlertMessage>
      <div className="ta-c">
        <button className="secondary-btn btn mb-3 mt-3" onClick={restart}>
          Start over
        </button>
      </div>
    </Fragment>
  ) : (
    <FindVerifiedPerson
      key={`FindVerifiedPerson-${key}`}
      onChange={loginAndRedirect}
      defaultValue={defaultValue}
      autoSubmitDefaultValue={key === 0 && autoSubmitDefaultValue}
      emailOnly={emailOnly}
      textOverrides={textOverrides}
    />
  )
}
